/**
 * Variant-aware price sorting for Dawn collection pages.
 * Sorts by the effective variant price for active Size + Option filters.
 */

(function () {
  'use strict';

  const GRID_SELECTOR = '#product-grid';
  const CARD_SELECTOR = '.grid__item';
  const PRICE_DATA_SELECTOR = '.product-price-data';
  const PAGINATION_SELECTOR = '.pagination-wrapper, .pagination, nav.pagination';

  const DEFAULT_SIZE = 'QUEEN';
  const DEFAULT_OPTION = 'NO BOX SPRING';
  const FALLBACK_OPTION = 'NO BOX';

  let allProducts = [];
  let perPage = 0;
  let totalPages = 1;
  let rebuildToken = 0;
  let selfMutating = false;
  let mutationDebounce = null;
  let gridObserver = null;
  let observedContainer = null;
  // True until renderPage() first rewrites the grid. While true, the cards in
  // the DOM are still exactly what the server rendered, so they can be read
  // directly instead of refetching them.
  let domPristine = true;
  let catalogCache = null;
  let activeRequest = null;
  let facetRequestPending = false;
  const CACHE_TTL = 120000;

  const mobileViewport = window.matchMedia('(max-width:749px)');
  let nativeMobileCatalog = null;
  let renderedPage = 1;
  let renderToken = 0;
  function isMobileCollection() {return mobileViewport.matches && document.body.classList.contains('mv-page-collection');}
  function displayPageSize(serverSize) {return isMobileCollection() ? 6 : serverSize;}
  function nativeCatalogKey() {
    const url = new URL(location.href);url.searchParams.delete('page');url.searchParams.delete('m_page');url.searchParams.delete('section_id');url.searchParams.sort();return url.href;
  }
  function seedNativeMobileCatalog() {
    const grid=findGrid(),key=nativeCatalogKey();
    if(!grid||!grid.dataset.mvServerPageSize)return null;
    if(!nativeMobileCatalog||nativeMobileCatalog.key!==key)nativeMobileCatalog={key,serverSize:Number(grid.dataset.mvServerPageSize),count:Number(grid.dataset.mvResultCount),pages:new Map()};
    if(domPristine){const page=Number(grid.dataset.mvSourcePage)||1;nativeMobileCatalog.pages.set(page,Array.from(grid.querySelectorAll(CARD_SELECTOR)).map(cardToProduct));}
    return nativeMobileCatalog;
  }
  async function loadNativeSlice(page,token,signal) {
    const catalog=nativeMobileCatalog,start=(page-1)*perPage,end=Math.min(start+perPage,catalog.count),needed=[];
    for(let offset=start;offset<end;offset+=1){const sourcePage=Math.floor(offset/catalog.serverSize)+1;if(!catalog.pages.has(sourcePage)&&!needed.includes(sourcePage))needed.push(sourcePage);}
    const htmlPages=await fetchPagesLimited(needed,2,catalog.key,signal);
    if(token!==rebuildToken||catalog!==nativeMobileCatalog)return false;
    htmlPages.forEach((html,index)=>{if(!html)throw Error('Could not load collection page '+needed[index]);catalog.pages.set(needed[index],extractCards(html).map(cardToProduct));});
    allProducts=new Array(catalog.count);
    catalog.pages.forEach((products,sourcePage)=>products.forEach((product,index)=>{allProducts[(sourcePage-1)*catalog.serverSize+index]=product;}));
    for(let offset=start;offset<end;offset+=1)if(!allProducts[offset])throw Error('Collection page incomplete; please retry.');
    return true;
  }
  function positivePage(value) {const page=Number(value);return Number.isInteger(page)&&page>0?page:1;}
  function requestedDisplayPage(params=new URLSearchParams(location.search)) {return positivePage(isMobileCollection() ? (params.get('m_page')||params.get('page')) : params.get('page'));}
  function setPageParams(url,page) {
    if(isMobileCollection()) {const serverSize=Number(findGrid()?.dataset.mvServerPageSize)||9;url.searchParams.set('m_page',page);url.searchParams.set('page',Math.floor((page-1)*6/serverSize)+1);}
    else {url.searchParams.delete('m_page');url.searchParams.set('page',page);}
  }
  function pageHref(page) {const url=withoutAutoFilterParams(new URL(location.href));setPageParams(url,page);return url.pathname+url.search;}

  function syncCollectionCanonical(url=new URL(location.href)) {
    if(!document.body.classList.contains('mv-page-collection'))return;
    const link=document.querySelector('link[rel="canonical"]');
    if(!link)return;
    // Match Liquid's canonical: clean collection path and native Shopify page.
    // m_page only selects the six-card mobile presentation of that source page.
    const canonical=new URL(link.href);
    canonical.search='';canonical.hash='';
    const page=positivePage(url.searchParams.get('page'));
    if(page>1)canonical.searchParams.set('page',page);
    if(link.href!==canonical.href)link.href=canonical.href;
  }

  function useClientPagination(event) {
    return !event.defaultPrevented && (!event.button || event.button===0)
      && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  }

  function preserveRequestedPage(searchParams,updateURLHash) {
    const params=new URLSearchParams(searchParams);
    if(!document.body.classList.contains('mv-page-collection'))return params.toString();
    // New facet/sort forms omit pagination. Keep the current position, while
    // explicit page targets and history navigation retain their own intent.
    if(updateURLHash!==false&&!params.has('page')&&!params.has('m_page')){
      const current=new URLSearchParams(location.search);
      ['page','m_page'].forEach(key=>{if(current.has(key))params.set(key,current.get(key));});
    }
    return params.toString();
  }

  function recoverEmptyCollectionPage(controller) {
    const grid=findGrid();
    if(!document.body.classList.contains('mv-page-collection')||!grid
      ||!grid.classList.contains('collection--empty')||grid.querySelector(CARD_SELECTOR))return false;
    const url=new URL(location.href);
    if(positivePage(url.searchParams.get('page'))===1&&positivePage(url.searchParams.get('m_page'))===1)return false;
    // The server confirmed this requested page is empty. Load page 1 once:
    // a narrower filter may still have products there. A full response restores
    // Dawn's facet/count containers omitted by its empty branch. Page 1 is final.
    setPageParams(url,1);
    window.location.replace(url.toString());
    return true;
  }

  function catalogKey(search = window.location.search) {
    const params = catalogSourceUrl(search).searchParams;
    const entries = Array.from(params.entries()).filter(([key, value]) =>
      value !== '' && !['sort_by', 'page', 'm_page', 'section_id', '_pricesort_auto'].includes(key)
    ).sort(([a, av], [b, bv]) => a.localeCompare(b) || av.localeCompare(bv));
    return window.location.pathname + '?' + new URLSearchParams(entries).toString();
  }

  function cachedCatalog(search = window.location.search) {
    return catalogCache && catalogCache.key === catalogKey(search)
      && Date.now() - catalogCache.time < CACHE_TTL ? catalogCache : null;
  }

  function findGrid() {
    return document.querySelector(GRID_SELECTOR);
  }

  function findStableContainer() {
    const grid = findGrid();
    if (!grid || !grid.querySelector(CARD_SELECTOR)) return null;
    const container = grid.closest('[id*="ProductGridContainer"]') || grid.parentElement;
    return container === document.body ? grid : container;
  }

  function findPagination() {
    return document.querySelector('.pagination-wrapper') || document.querySelector(PAGINATION_SELECTOR);
  }

  function normalize(value) {
    return String(value || '').trim().toUpperCase();
  }

  function getSelectedVariantOptions() {
    const params = new URLSearchParams(window.location.search);
    const selected = {
      size: [],
      option: []
    };

    params.forEach((value, key) => {
      const lowerKey = key.toLowerCase();
      if (!lowerKey.startsWith('filter.v.option.')) return;

      const optionName = lowerKey.replace(/^filter\.v\.option\./, '');
      if (optionName === 'size') {
        selected.size.push(normalize(value));
      } else if (optionName === 'option' || optionName.includes('box')) {
        selected.option.push(normalize(value));
      }
    });

    return selected;
  }

  function getCollectionTitleSizeValue() {
    const grid = findGrid();
    const container = grid ? grid.closest('[data-collection-title]') : null;
    const title = container ? container.dataset.collectionTitle : '';
    if (!title || !title.includes(' Size ')) return null;

    const sizeValue = title.split(' Size ')[0].trim();
    return sizeValue || null;
  }

  function getCollectionDefaultSizes() {
    const sizeValue = getCollectionTitleSizeValue();
    return sizeValue ? [normalize(sizeValue)] : null;
  }

  const AUTOFILTER_PARAM = '_pricesort_auto';

  function implicitCollectionSize(params=new URLSearchParams(location.search)) {
    if(params.has('filter.v.option.size')||params.has('sort_by'))return null;
    return getCollectionTitleSizeValue();
  }

  function catalogSourceUrl(search=location.search) {
    const url=new URL(location.href);url.search=search;
    const size=implicitCollectionSize(url.searchParams);
    // Preserve the existing size-filtered, variant-aware price ordering without
    // navigating the clean collection into a robots-blocked filter/sort URL.
    if(size){url.searchParams.set('filter.v.option.size',normalize(size));url.searchParams.set('sort_by','price-ascending');}
    url.searchParams.delete(AUTOFILTER_PARAM);
    return url;
  }

  function syncImplicitCollectionControls() {
    const size=implicitCollectionSize();
    if(!size)return;
    // The former redirect loaded these native controls preselected. Keep that
    // state so the next shopper facet change submits the same size and sort.
    document.querySelectorAll('select[name="sort_by"]').forEach(select=>{select.value='price-ascending';});
    document.querySelectorAll('input[name="filter.v.option.size"]').forEach(input=>{
      if(normalize(input.value)===normalize(size))input.checked=true;
    });
  }

  function withoutAutoFilterParams(url) {
    if(url.searchParams.get(AUTOFILTER_PARAM)!=='1')return url;
    url.searchParams.delete(AUTOFILTER_PARAM);
    const size=getCollectionTitleSizeValue();
    if(size&&normalize(url.searchParams.get('filter.v.option.size'))===normalize(size))url.searchParams.delete('filter.v.option.size');
    if(url.searchParams.get('sort_by')==='price-ascending')url.searchParams.delete('sort_by');
    return url;
  }

  function cleanupAutoFilterUrl() {
    const url=withoutAutoFilterParams(new URL(location.href));
    if(url.href===location.href)return;
    const state={...history.state,searchParams:url.searchParams.toString()};
    history.replaceState(state,'',url);
    syncCollectionCanonical(url);
  }

  function getEffectiveSortBy() {
    const urlSort = new URLSearchParams(window.location.search).get('sort_by');
    if (urlSort) return urlSort.toLowerCase();
    if (implicitCollectionSize()) return 'price-ascending';

    // No sort_by in the URL means the collection's configured default sort
    // is in effect. The rendered <select> reflects that default correctly.
    const select = document.querySelector('select[name="sort_by"]');
    if (select && select.value) return select.value.toLowerCase();

    return '';
  }

  function getSortDirection() {
    return getEffectiveSortBy().includes('desc') ? 'desc' : 'asc';
  }

  function isPriceSort() {
    return getEffectiveSortBy().startsWith('price');
  }

  function parsePriceBucketLabel(label) {
    const text = String(label || '').trim();

    const upToMatch = text.match(/up\s*to\s*\$?([\d,]+)/i);
    if (upToMatch) {
      return { min: null, max: Number(upToMatch[1].replace(/,/g, '')) };
    }

    const andUpMatch = text.match(/\$?([\d,]+)\s*and\s*up/i);
    if (andUpMatch) {
      return { min: Number(andUpMatch[1].replace(/,/g, '')), max: null };
    }

    const rangeMatch = text.match(/\$?([\d,]+)\s*-\s*\$?([\d,]+)/);
    if (rangeMatch) {
      return {
        min: Number(rangeMatch[1].replace(/,/g, '')),
        max: Number(rangeMatch[2].replace(/,/g, ''))
      };
    }

    return { min: null, max: null };
  }

  function getPriceRangeFilter() {
    const params = new URLSearchParams(window.location.search);
    const gte = params.get('filter.v.price.gte');
    const lte = params.get('filter.v.price.lte');

    // URL values are dollars; variant prices are in cents.
    let min = gte && Number.isFinite(Number(gte)) ? Number(gte) * 100 : null;
    let max = lte && Number.isFinite(Number(lte)) ? Number(lte) * 100 : null;

    if (min === null && max === null) {
      const bucketValue = params.get('filter.p.m.custom.price_range');
      if (bucketValue) {
        const bucket = parsePriceBucketLabel(bucketValue);
        min = bucket.min !== null ? bucket.min * 100 : null;
        max = bucket.max !== null ? bucket.max * 100 : null;
      }
    }

    return { min, max };
  }

  function hasPriceRangeFilter() {
    const { min, max } = getPriceRangeFilter();
    return min !== null || max !== null;
  }

  function shouldRebuild() {
    return isPriceSort() || hasPriceRangeFilter() || isMobileCollection() || !!nativeMobileCatalog;
  }

  function filterByPriceRange() {
    const { min, max } = getPriceRangeFilter();
    if (min === null && max === null) return;

    // Shopify's native price filter matches on the product's overall
    // price_min/price_max across all variants, not the effective/displayed
    // variant price — so it can include products whose shown price is
    // actually outside the requested range. Re-filter on the effective price.
    allProducts = allProducts.filter((product) => {
      if (!isFinite(product.price)) return false;
      if (min !== null && product.price < min) return false;
      if (max !== null && product.price > max) return false;
      return true;
    });
  }

  function waitForGridReady(cb, tries = 0) {
    const grid = findGrid();
    if (!grid || !grid.querySelector(CARD_SELECTOR)) return;
    if (grid.querySelectorAll(PRICE_DATA_SELECTOR).length === 0) {
      if (tries < 20) {
        return setTimeout(() => waitForGridReady(cb, tries + 1), 150);
      }
      hideLoading();
      return;
    }

    cb();
  }

  function readVariantData(card) {
    const el = card.querySelector(PRICE_DATA_SELECTOR);
    if (!el) return [];

    try {
      return JSON.parse(el.dataset.variantPrices || '[]');
    } catch (error) {
      console.warn('[price-sort] invalid variant data', error);
      return [];
    }
  }

  function findEffectiveVariant(variants, selected = getSelectedVariantOptions()) {
    if (!variants.length) return null;

    const sizes = selected.size.length
      ? selected.size
      : (getCollectionDefaultSizes() || [DEFAULT_SIZE]);
    const options = selected.option.length ? selected.option : [DEFAULT_OPTION, FALLBACK_OPTION, ''];

    for (const size of sizes) {
      for (const option of options) {
        const match = variants.find((variant) => {
          const sameSize = normalize(variant.option1) === size;
          const sameOption = option === '' || normalize(variant.option2) === option;
          return sameSize && sameOption;
        });

        if (match) return match;
      }
    }

    for (const size of sizes) {
      const sizeMatch = variants.find((variant) => normalize(variant.option1) === size);
      if (sizeMatch) return sizeMatch;
    }

    return variants[0];
  }

  function computePrice(variants, selected = getSelectedVariantOptions()) {
    const variant = findEffectiveVariant(variants, selected);
    return variant && Number.isFinite(Number(variant.price)) ? Number(variant.price) : Infinity;
  }

  function cardToProduct(card) {
    const variants = readVariantData(card);

    return {
      html: card.outerHTML,
      variants,
      price: computePrice(variants)
    };
  }

  function getSectionId() {
    const grid = findGrid();
    return grid ? (grid.dataset.id || null) : null;
  }

  async function fetchPage(page, sourceUrl = window.location.href, signal) {
    const url = new URL(sourceUrl);
    url.searchParams.set('page', page);

    // Using the section rendering API returns only the section HTML (~20–50 KB)
    // instead of the full storefront page (~200–400 KB), making each fetch
    // significantly faster when there are multiple pages to load.
    const sectionId = getSectionId();
    if (sectionId) url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), { signal });
    if (!response.ok) return null;

    return response.text();
  }

  function parseHtml(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function extractCards(html) {
    const grid = parseHtml(html).querySelector(GRID_SELECTOR);
    if (!grid) return [];

    return Array.from(grid.querySelectorAll(CARD_SELECTOR));
  }

  function detectTotalPagesFromHtml(html) {
    const pagination = parseHtml(html).querySelector(PAGINATION_SELECTOR);
    if (!pagination) return 1;

    let max = 1;
    pagination.querySelectorAll('a').forEach((link) => {
      try {
        const page = new URL(link.href).searchParams.get('page');
        if (page) max = Math.max(max, Number(page));
      } catch (error) {
        // Ignore non-URL pagination links.
      }
    });

    return max;
  }

  // Returns the server-rendered page-1 cards straight from the DOM, or null when
  // that isn't safe. Only valid on page 1 and only before renderPage() has run —
  // afterwards the grid holds a sorted subset, not the server's page 1, and
  // reusing it would corrupt allProducts.
  function readPristineGridCards() {
    if (!domPristine) return null;
    if (implicitCollectionSize()) return null;
    if ((Number(new URLSearchParams(window.location.search).get('page')) || 1) !== 1) return null;

    const grid = findGrid();
    if (!grid) return null;

    const cards = Array.from(grid.querySelectorAll(CARD_SELECTOR));
    if (!cards.length) return null;

    // If any tile lacks variant data (promo tile, app-injected card, partially
    // rendered grid) fall back to fetching rather than building a bad list.
    if (cards.some((card) => !card.querySelector(PRICE_DATA_SELECTOR))) return null;

    return cards;
  }

  function detectTotalPagesFromDom() {
    const pagination = findPagination();
    if (!pagination) return 1;

    let max = 1;
    pagination.querySelectorAll('a').forEach((link) => {
      try {
        const page = new URL(link.href).searchParams.get('page');
        if (page) max = Math.max(max, Number(page));
      } catch (error) {
        // Ignore non-URL pagination links.
      }
    });

    return max;
  }

  async function fetchPagesLimited(pageNumbers, concurrency = 4, sourceUrl, signal) {
    const results = new Array(pageNumbers.length);
    let next = 0;

    async function worker() {
      while (next < pageNumbers.length) {
        const index = next++;
        if (signal?.aborted) return;
        results[index] = await fetchPage(pageNumbers[index], sourceUrl, signal);
      }
    }

    const workers = Array.from({ length: Math.min(concurrency, pageNumbers.length) }, worker);
    await Promise.all(workers);

    return results;
  }

  async function buildAllProducts(token, signal) {
    const sourceUrl = catalogSourceUrl().href;
    const key = catalogKey();
    const cached = isPriceSort() ? cachedCatalog() : null;
    if (cached) return { products: cached.products, perPage: cached.perPage, cached: true };
    const products = [];
    let pages = 1;

    // Fast path: on a first load of page 1 the grid already contains exactly the
    // HTML fetchPage(1) would return, so reuse it. Saves a full round trip —
    // single-page collections then need no network at all before rendering.
    let page1Cards = readPristineGridCards();

    if (page1Cards) {
      pages = detectTotalPagesFromDom();
    } else {
      const page1Html = await fetchPage(1, sourceUrl, signal);
      if (token !== rebuildToken) return false;
      if (!page1Html) return false;

      page1Cards = extractCards(page1Html);
      pages = detectTotalPagesFromHtml(page1Html);
    }

    const currentGrid = findGrid();
    const pageSize = page1Cards.length || (currentGrid ? currentGrid.querySelectorAll(CARD_SELECTOR).length : 0);
    page1Cards.forEach((card) => products.push(cardToProduct(card)));

    const pageNumbers = [];
    for (let page = 2; page <= pages; page += 1) pageNumbers.push(page);

    // The mobile first screen waits for variant-aware ordering. Start all
    // remaining source pages together for small/medium collections.
    const htmlPages = await fetchPagesLimited(pageNumbers, isMobileCollection() ? 8 : 4, sourceUrl, signal);
    if (token !== rebuildToken) return false;

    htmlPages.forEach((html, index) => {
      if (!html) {
        throw new Error('Could not load collection page ' + pageNumbers[index]);
      }

      extractCards(html).forEach((card) => products.push(cardToProduct(card)));
    });

    catalogCache = isPriceSort() ? { key, time: Date.now(), products, perPage: pageSize } : null;
    return { products, perPage: pageSize };
  }

  function sortProducts() {
    const selected = getSelectedVariantOptions();

    allProducts.forEach((product) => {
      product.price = computePrice(product.variants, selected);
    });

    filterByPriceRange();

    // Only re-sort when price sorting is actually active — otherwise preserve
    // Shopify's own fetched order (e.g. best-selling, relevance) and just
    // leave the out-of-range filtering applied above.
    if (!isPriceSort()) return;

    const direction = getSortDirection();
    allProducts.sort((a, b) => {
      const priceA = isFinite(a.price) ? a.price : Infinity;
      const priceB = isFinite(b.price) ? b.price : Infinity;

      return direction === 'asc' ? priceA - priceB : priceB - priceA;
    });
  }

  function showLoading() {
    const grid = findGrid();
    if (!grid) return;
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      container.setAttribute('aria-busy', 'true');
      container.dataset.priceSortState = 'loading';
    }

    grid.style.transition = 'none';
    grid.style.opacity = '0';
    grid.style.pointerEvents = 'none';

    if (!document.getElementById('price-sort-loader')) {
      if (!document.getElementById('price-sort-style')) {
        const style = document.createElement('style');
        style.id = 'price-sort-style';
        style.textContent = '@keyframes price-sort-spin { to { transform: rotate(360deg); } }';
        document.head.appendChild(style);
      }
      const loader = document.createElement('div');
      loader.id = 'price-sort-loader';
      loader.style.cssText = [
        'position:fixed',
        'top:50%',
        'left:50%',
        'transform:translate(-50%,-50%)',
        'z-index:9999',
        'width:48px',
        'height:48px',
        'border-radius:50%',
        'border:3px solid rgba(0,0,0,0.12)',
        'border-top-color:#212529',
        'animation:price-sort-spin 0.75s linear infinite'
      ].join(';');
      document.body.appendChild(loader);
    }
  }

  function hideLoading() {
    const grid = findGrid();
    const container = document.getElementById('ProductGridContainer');
    if (container) {
      container.setAttribute('aria-busy', 'false');
      container.dataset.priceSortState = 'ready';
    }

    const veil = document.getElementById('price-sort-veil');
    if (veil) veil.remove();

    const loader = document.getElementById('price-sort-loader');
    if (loader) loader.remove();
    if (!grid) return;

    // Reveal once; do not add a second forced fade/reflow after Dawn's update.
    grid.style.transition = '';
    grid.style.opacity = '';
    grid.style.pointerEvents = '';
  }

  function updateVariantLabels() {
    const grid = findGrid();
    if (!grid) return;

    const selected = getSelectedVariantOptions();

    grid.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      const variant = findEffectiveVariant(readVariantData(card), selected);
      if (!variant) return;

      const variantName = variant.option1 || '';
      const label = card.querySelector('.price__sale small');
      if (label && variantName) label.textContent = variantName.toUpperCase();
    });
  }

  function updatePaginationUI(active) {
    let wrapper = findPagination();
    if (!wrapper && isMobileCollection() && totalPages > 1) {wrapper=document.createElement('div');wrapper.className='pagination-wrapper';wrapper.dataset.mvAdaptivePagination='true';findGrid().after(wrapper);}
    if (!wrapper) return;
    if (wrapper.dataset.mvAdaptivePagination) wrapper.hidden=totalPages<=1;

    const nav = document.createElement('nav');
    nav.className = 'pagination custom-pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', 'Pagination');

    const ul = document.createElement('ul');
    ul.className = 'pagination__list list-unstyled';
    ul.setAttribute('role', 'list');

    const prevIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 13" fill="none" width="24" height="11">
        <path d="M7.5 1L1 6.5L7.5 12" stroke="currentColor" stroke-width="2"/>
        <path d="M1 6.5H25" stroke="currentColor" stroke-width="2"/>
      </svg>
    `;

    const nextIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 26 13" fill="none" width="24" height="11">
        <path d="M18.5 1L25 6.5L18.5 12" stroke="currentColor" stroke-width="2"/>
        <path d="M1 6.5H25" stroke="currentColor" stroke-width="2"/>
      </svg>
    `;

    if (active > 1) {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'btn-navigate prev';
      link.href = pageHref(active-1);
      link.setAttribute('aria-label', 'Previous page');
      link.innerHTML = `${prevIcon}<span class="label">PREV</span>`;
      link.addEventListener('click', (event) => {
        if(!useClientPagination(event))return;
        event.preventDefault();
        renderPage(active - 1);
      });
      li.appendChild(link);
      ul.appendChild(li);
    }

    const pages = [1];
    const delta = 1;

    for (let page = active - delta; page <= active + delta; page += 1) {
      if (page > 1 && page < totalPages) pages.push(page);
    }

    if (totalPages > 1) pages.push(totalPages);

    let lastPage = 0;
    [...new Set(pages)].sort((a, b) => a - b).forEach((page) => {
      if (page - lastPage > 1) {
        const li = document.createElement('li');
        const dots = document.createElement('span');
        dots.className = 'btn-paginate dots';
        dots.textContent = '...';
        li.appendChild(dots);
        ul.appendChild(li);
      }

      const li = document.createElement('li');

      if (page === active) {
        const span = document.createElement('span');
        span.className = 'btn-paginate active';
        span.setAttribute('aria-current', 'page');
        span.textContent = page;
        li.appendChild(span);
      } else {
        const link = document.createElement('a');
        link.className = 'btn-paginate';
        link.href = pageHref(page);
        link.textContent = page;
        link.setAttribute('aria-label', `Page ${page}`);
        link.addEventListener('click', (event) => {
          if(!useClientPagination(event))return;
          event.preventDefault();
          renderPage(page);
        });
        li.appendChild(link);
      }

      ul.appendChild(li);
      lastPage = page;
    });

    if (active < totalPages) {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'btn-navigate next';
      link.href = pageHref(active+1);
      link.setAttribute('aria-label', 'Next page');
      link.innerHTML = `<span class="label">NEXT</span>${nextIcon}`;
      link.addEventListener('click', (event) => {
        if(!useClientPagination(event))return;
        event.preventDefault();
        renderPage(active + 1);
      });
      li.appendChild(link);
      ul.appendChild(li);
    }

    nav.appendChild(ul);
    wrapper.innerHTML = '';
    wrapper.appendChild(nav);
  }

  async function renderPage(page, historyMode = 'push') {
    const grid = findGrid();
    if (!grid || !perPage) return;

    const safePage = Math.max(1, Math.min(page, totalPages));
    const sequence=++renderToken;
    if(nativeMobileCatalog){
      const token=rebuildToken;showLoading();
      try {if(!await loadNativeSlice(safePage,token,activeRequest?.signal)||sequence!==renderToken)return;}
      catch(error){if(error.name!=='AbortError')console.warn('[mobile-pagination]',error.message);return;}
      finally {if(sequence===renderToken)hideLoading();}
    }
    renderedPage=safePage;
    const start = (safePage - 1) * perPage;
    const slice = allProducts.slice(start, start + perPage);

    // The grid is about to stop matching the server's page 1.
    domPristine = false;
    selfMutating = true;
    // On the search page, #product-grid is a wrapper <div> containing a
    // loading-overlay and a <ul class="grid">. Writing cards directly to
    // the div destroys the <ul> wrapper, breaking the flex layout. Write
    // to the inner <ul> when it exists; fall back to the grid element itself
    // on collection pages where #product-grid is already the <ul>.
    const cardContainer = grid.querySelector('ul.grid') || grid;
    cardContainer.innerHTML = slice.map((product) => product.html).join('');
    updateVariantLabels();
    updatePaginationUI(safePage);
    observeGridChanges();
    setTimeout(() => { selfMutating = false; }, 0);

    const url = new URL(window.location.href);
    setPageParams(url,safePage);
    const state = { ...history.state, page: safePage, searchParams: url.searchParams.toString() };
    if (historyMode === 'push') history.pushState(state, '', url);
    else history.replaceState(state, '', url);
    syncCollectionCanonical(url);
    // Dawn skips popstate when its previous params already match the target.
    // Keep that cursor current for desktop client pagination as well as mobile.
    const facets=window.customElements?.get('facet-filters-form')||window.FacetFiltersForm;
    if(facets)facets.searchParamsPrev=url.searchParams.toString();
    const container = document.getElementById('ProductGridContainer');
    if (container) container.dataset.priceSortRender = String((Number(container.dataset.priceSortRender) || 0) + 1);
  }

  async function rebuild() {
    const token = rebuildToken + 1;
    rebuildToken = token;

    activeRequest?.abort();
    const request = activeRequest = new AbortController();
    showLoading();
    try {
      const requestedPage = requestedDisplayPage();
      if (!isPriceSort() && !hasPriceRangeFilter() && (isMobileCollection() || nativeMobileCatalog)) {
        const catalog=seedNativeMobileCatalog();if(!catalog)return;
        perPage=displayPageSize(catalog.serverSize);totalPages=Math.max(1,Math.ceil(catalog.count/perPage));
        await renderPage(requestedPage,'replace');
        return;
      }
      nativeMobileCatalog=null;
      const built = await buildAllProducts(token, request.signal);
      if (!built || token !== rebuildToken) return;
      allProducts = built.products.map(product => ({ ...product }));
      perPage = displayPageSize(built.perPage);
      sortProducts();
      totalPages = perPage ? Math.ceil(allProducts.length / perPage) : 1;
      // Dawn already wrote the navigation entry. Do not create a second one.
      await renderPage(requestedPage, 'replace');
      const container = document.getElementById('ProductGridContainer');
      if (container) container.dataset.priceSortSource = built.cached ? 'cache' : 'fresh';
      cleanupAutoFilterUrl();
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('[price-sort]', error.message);
    } finally {
      if (token === rebuildToken) {
        activeRequest = null;
        hideLoading();
      }
    }

    window.__priceSort = {
      ran: true,
      isPriceSort: isPriceSort(),
      priceRangeFilter: getPriceRangeFilter(),
      gridFound: !!findGrid(),
      productCount: allProducts.length,
      perPage,
      totalPages,
      prices: allProducts.map((product) => product.price)
    };
  }

  function scheduleGridRebuild() {
    clearTimeout(mutationDebounce);
    if (!findGrid() || !shouldRebuild() || !findGrid().querySelector(CARD_SELECTOR)) {
      hideLoading();
      return;
    }
    // Run in the next task, with the intermediate native grid already hidden.
    showLoading();
    mutationDebounce = setTimeout(() => {
      mutationDebounce = null;
      if (findGrid() && shouldRebuild()) waitForGridReady(rebuild);
    }, 0);
  }

  function sortCachedPage(controller, searchParams, updateURLHash) {
    const params = new URLSearchParams(searchParams);
    const sort = params.get('sort_by') || '';
    const cached = cachedCatalog(searchParams);
    if (!sort.startsWith('price-') || !cached || facetRequestPending || !findGrid()) return false;
    clearTimeout(mutationDebounce);
    rebuildToken += 1;
    activeRequest?.abort();
    activeRequest = null;
    controller.searchParamsPrev = searchParams;
    if (updateURLHash !== false) controller.updateURLHash(searchParams);
    document.querySelectorAll('select[name="sort_by"]').forEach(select => { select.value = sort; });
    allProducts = cached.products.map(product => ({ ...product }));
    nativeMobileCatalog=null;
    perPage = displayPageSize(cached.perPage);
    sortProducts();
    totalPages = perPage ? Math.ceil(allProducts.length / perPage) : 1;
    renderPage(requestedDisplayPage(params), 'replace');
    const container = document.getElementById('ProductGridContainer');
    if (container) container.dataset.priceSortSource = 'cache';
    controller.searchParamsPrev = new URLSearchParams(location.search).toString();
    hideLoading();
    return true;
  }

  function hookDawn() {
    // Dawn registers this class as a custom element, not necessarily on window.
    const controller = window.customElements?.get('facet-filters-form') || window.FacetFiltersForm;
    const owner = typeof controller?.renderProductGridContainer === 'function'
      ? controller
      : controller?.prototype;
    const original = owner?.renderProductGridContainer;
    if (typeof original !== 'function') return false;
    if (original.__priceSortHooked) return true;

    const nativeRenderPage = controller.renderPage;
    if (typeof nativeRenderPage === 'function') {
      controller.renderPage = function (searchParams, event, updateURLHash = true) {
        searchParams=preserveRequestedPage(searchParams,updateURLHash);
        if (sortCachedPage(controller, searchParams, updateURLHash)) return;
        clearTimeout(mutationDebounce);
        rebuildToken += 1;
        activeRequest?.abort();
        facetRequestPending = true;
        return nativeRenderPage.call(this, searchParams, event, updateURLHash);
      };
    }

    owner.renderProductGridContainer = function (...args) {
      const result = original.apply(this, args);
      facetRequestPending = false;
      // A genuine Dawn replacement is fresh server HTML, not our sorted slice.
      domPristine = true;
      if(recoverEmptyCollectionPage(controller))return result;
      observeGridChanges();
      scheduleGridRebuild();
      return result;
    };
    owner.renderProductGridContainer.__priceSortHooked = true;
    return true;
  }

  function connectFacets() {
    if (hookDawn()) return;
    // One registration notification replaces indefinite 100 ms polling.
    // It also handles a delayed facets script without a timing cutoff.
    if (window.customElements && document.querySelector('facet-filters-form')) {
      window.customElements.whenDefined('facet-filters-form').then(hookDawn);
    }
  }

  function observeGridChanges() {
    const target = findStableContainer();
    if (target === observedContainer) return;
    if (gridObserver) gridObserver.disconnect();
    gridObserver = null;
    observedContainer = null;
    if (!target) return;

    gridObserver = new MutationObserver((mutations) => {
      if (selfMutating) return;

      // Ignore updates inside cards (for example, review widgets).
      const relevant = mutations.some((mutation) => {
        const el = mutation.target;
        return !(el.closest && el.closest(CARD_SELECTOR));
      });
      if (!relevant) return;
      scheduleGridRebuild();
    });

    gridObserver.observe(target, { childList: true, subtree: true });
    observedContainer = target;
  }

  mobileViewport.addEventListener('change',()=>{
    if(!document.body.classList.contains('mv-page-collection')||!findGrid())return;
    const first=(renderedPage-1)*perPage;
    const serverSize=Number(findGrid().dataset.mvServerPageSize)||9;
    perPage=displayPageSize(serverSize);
    totalPages=Math.max(1,Math.ceil((nativeMobileCatalog?nativeMobileCatalog.count:allProducts.length)/perPage));
    if(allProducts.length||nativeMobileCatalog)renderPage(Math.floor(first/perPage)+1,'replace');
    else if(isMobileCollection())scheduleGridRebuild();
  });
  function initializeCollectionSorting() {
    // Keep only the lightweight facet lifecycle hook on an empty-results page:
    // clearing a filter can render a real grid later. No polling or body observer.
    cleanupAutoFilterUrl();
    connectFacets();
    const grid = findGrid();
    if (!grid || !grid.querySelector(CARD_SELECTOR)) return;
    syncImplicitCollectionControls();

    window.__priceSort = {
      ran: false,
      isPriceSort: isPriceSort(),
      priceRangeFilter: getPriceRangeFilter(),
      gridFound: true,
      priceDataCount: grid.querySelectorAll(PRICE_DATA_SELECTOR).length
    };

    if (shouldRebuild()) {
      showLoading();
      waitForGridReady(rebuild);
    }
    observeGridChanges();
  }
  // A deferred script already has the parsed DOM; do not wait for unrelated
  // deferred app scripts and widgets to finish before starting catalog fetches.
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',initializeCollectionSorting,{once:true});
  else initializeCollectionSorting();
})();
